import { Link, useParams } from "react-router-dom";
import ReaderGuidePage from "./ReaderGuidePage";
import { readerGuideByPath } from "../data/readerGuides20261006";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import ArticleAd from "../ads/ArticleAd";
import { robotCategories } from "../data/robotCategories";
import categoryReferences from "../data/categoryReferences20260922.json";
import { seoConfig } from "../data/seoConfig";
import { newsArticles } from "../data/newsArticles";
import { xRoboticsArticles } from "../data/xRoboticsArticles";

function TextPanel({ id, title, children }) {
  return (
    <section id={id} className="soft-card scroll-mt-28 p-5 sm:p-6">
      <h2 className="text-xl font-bold tracking-tight text-neutral-950">
        {title}
      </h2>
      <div className="mt-4 text-base leading-8 text-neutral-600">
        {children}
      </div>
    </section>
  );
}

function DetailList({ id, title, items }) {
  return (
    <section id={id} className="soft-card scroll-mt-28 p-5 sm:p-6">
      <h2 className="text-xl font-bold tracking-tight text-neutral-950">
        {title}
      </h2>
      <ul className="mt-4 grid gap-2 text-sm leading-6 text-neutral-600 sm:grid-cols-2">
        {items.map((item) => (
          <li key={item} className="rounded-2xl bg-neutral-50 px-4 py-3">
            {item}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ResearchList({ id, title, items }) {
  return (
    <section id={id} className="soft-card scroll-mt-28 p-5 sm:p-6">
      <h2 className="text-xl font-bold tracking-tight text-neutral-950">
        {title}
      </h2>
      <div className="mt-4 grid gap-3">
        {items.map((item, index) => (
          <p
            key={`${title}-${index}`}
            className="rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm leading-6 text-neutral-600"
          >
            <span className="mr-2 font-bold text-neutral-950">
              {String(index + 1).padStart(2, "0")}
            </span>
            {item}
          </p>
        ))}
      </div>
    </section>
  );
}

function PageNavigation({ category }) {
  const links = [
    ["overview", "Overview"],
    ["how-it-works", "How it works"],
    ["applications", "Applications"],
    ["technology", "Technology"],
    ["safety", "Safety"],
    ...(category.sources?.length ? [["sources", "Sources"]] : []),
  ];
  return (
    <nav className="soft-card p-5 sm:p-6" aria-label={`Sections in the ${category.name} guide`}>
      <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Find the answer faster</p>
      <h2 className="mt-2 text-xl font-bold tracking-tight text-neutral-950">Start with the part you need</h2>
      <div className="mt-4 flex flex-wrap gap-2">
        {links.map(([id, label]) => (
          <a key={id} href={`#${id}`} className="rounded-full border border-neutral-200 bg-neutral-50 px-4 py-2 text-sm font-bold text-neutral-800 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700">{label}</a>
        ))}
      </div>
    </nav>
  );
}

function QuickDecisionSummary({ category }) {
  const cards = [
    ["Plain definition", category.definition],
    ["Example tasks", category.useCases.slice(0, 3).join("; ")],
    ["Main deployment risk", category.limitations[0]],
    ["Measure in a pilot", category.evaluationMetrics.slice(0, 4).join(", ")],
  ];
  return (
    <section className="soft-card p-5 sm:p-6" aria-labelledby="quick-decision-title">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Quick decision summary</p>
      <h2 id="quick-decision-title" className="mt-2 text-xl font-bold tracking-tight text-neutral-950">What to know before reading the full guide</h2>
      <div className="mt-5 grid gap-3 md:grid-cols-2">
        {cards.map(([label, value]) => (
          <div key={label} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
            <h3 className="text-xs font-black uppercase tracking-[0.14em] text-neutral-500">{label}</h3>
            <p className="mt-2 text-sm leading-7 text-neutral-700">{value}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function AgvDecisionGuide() {
  const rows = [
    ["Route behavior", "Follows a defined guide path or permitted route network.", "Plans through a mapped operating area and can replan within its rules."],
    ["Blocked aisle", "Usually slows or stops until the route clears or recovery is requested.", "May calculate another safe route when the map and vehicle capability allow it."],
    ["Facility changes", "Guide paths may need engineering and revalidation.", "Maps, zones and missions still need updating, testing and validation."],
    ["Strongest fit", "Stable, repetitive flows with predictable handoff points.", "Changing routes, variable destinations and busier shared spaces."],
  ];
  const checks = [
    ["Flow", "Loaded missions per hour, empty travel, route length, peak queues and handoff time."],
    ["Vehicle", "Rated payload including the top module, turning envelope, gradients, floor joints, doors and lifts."],
    ["Safety", "Stopping distance at rated load and speed, protective fields, emergency stops and operating-zone risk assessment."],
    ["Operations", "Charge strategy, recovery owner, spare coverage, service response and restart procedure."],
    ["Integration", "WMS, MES, PLC, conveyor, door and elevator interfaces; traffic control; supported VDA 5050 version."],
  ];
  return (
    <section className="soft-card overflow-hidden p-5 sm:p-6" aria-labelledby="agv-decision-title">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Buyer decision guide</p>
      <h2 id="agv-decision-title" className="mt-2 text-2xl font-bold tracking-tight text-neutral-950">AGV or AMR choose from the workflow, not the label</h2>
      <p className="mt-4 max-w-4xl text-base leading-8 text-neutral-700">AGV and AMR terminology overlaps across vendors. Compare route behavior, recovery, traffic control and operating limits in the proposed facility. A product label does not establish safety or prove throughput.</p>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-200">
        <table className="min-w-[780px] w-full border-collapse text-left text-sm leading-6">
          <caption className="sr-only">Practical comparison of AGV and AMR deployment characteristics</caption>
          <thead className="bg-neutral-950 text-white"><tr><th className="px-4 py-3">Decision point</th><th className="px-4 py-3">Traditional AGV</th><th className="px-4 py-3">AMR pattern</th></tr></thead>
          <tbody className="divide-y divide-neutral-200 bg-white">{rows.map(([label, agv, amr]) => <tr key={label}><th className="px-4 py-4 align-top font-black text-neutral-950">{label}</th><td className="px-4 py-4 align-top text-neutral-700">{agv}</td><td className="px-4 py-4 align-top text-neutral-700">{amr}</td></tr>)}</tbody>
        </table>
      </div>
      <h3 className="mt-8 text-lg font-black text-neutral-950">Five measurements for a useful pilot</h3>
      <div className="mt-4 grid gap-3 md:grid-cols-2">{checks.map(([label, value]) => <div key={label} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4"><p className="font-black text-neutral-950">{label}</p><p className="mt-2 text-sm leading-7 text-neutral-700">{value}</p></div>)}</div>
      <div className="mt-6 rounded-2xl border border-blue-200 bg-blue-50 p-5">
        <h3 className="font-black text-neutral-950">Use cost per completed loaded mission</h3>
        <p className="mt-2 text-sm leading-7 text-neutral-700">Annualize the vehicle, integration and infrastructure costs; add service, energy and recovery labor; then divide by completed loaded missions. Keep blocked and manually recovered missions visible.</p>
        <p className="mt-3 overflow-x-auto rounded-xl bg-white px-4 py-3 font-mono text-xs font-bold text-neutral-800">cost per useful mission = annual operating cost / completed loaded missions</p>
      </div>
    </section>
  );
}


function AmrDecisionGuide() {
  const types = [
    ["Person-to-goods", "Robot carries totes or orders between pick locations", "Walking distance, wait time at pick faces, tote capacity, human handoff time"],
    ["Goods-to-person", "Robot moves racks, shelves, or containers to a workstation", "Storage density, station utilization, queueing, rack interface, replenishment"],
    ["Conveyor-top / unit load", "Robot transfers totes or cartons between fixed equipment", "Docking accuracy, conveyor handshake, queue capacity, failed transfer recovery"],
    ["Pallet / fork AMR", "Robot moves pallets or engages loads with forks", "Load center, floor quality, fork alignment, pallet condition, aisle width, braking"],
  ];
  const pilot = [
    ["Throughput", "Completed loaded missions/hour plus 95th-percentile mission time during peak traffic."],
    ["Autonomy", "Manual interventions per 100 missions, classified by navigation, handoff, payload, network, or recovery."],
    ["Traffic", "Minutes lost to blocked aisles and intersection waiting; do not hide congestion inside average mission time."],
    ["Handoffs", "First-attempt docking and transfer success at conveyors, racks, pallets, doors, lifts, and chargers."],
    ["Economics", "Annualized system cost divided by completed loaded missions, including recovery labor and support."],
  ];
  return (
    <section className="soft-card overflow-hidden p-5 sm:p-6" aria-labelledby="amr-decision-title">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">AMR selection guide</p>
      <h2 id="amr-decision-title" className="mt-2 text-2xl font-bold tracking-tight text-neutral-950">Choose the AMR from the load flow, not from maximum speed</h2>
      <p className="mt-4 max-w-4xl text-base leading-8 text-neutral-700">Start with the material movement that exists today. Record origins, destinations, payload geometry, peak missions, aisle conflicts, handoff equipment, charging opportunities, and who owns recovery. A faster vehicle can produce less useful throughput if it spends more time waiting at intersections or failed docks.</p>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-200">
        <table className="min-w-[820px] w-full border-collapse text-left text-sm leading-6">
          <caption className="sr-only">Warehouse AMR types and buying questions</caption>
          <thead className="bg-neutral-950 text-white"><tr><th className="px-4 py-3">AMR pattern</th><th className="px-4 py-3">Typical job</th><th className="px-4 py-3">Verify before buying</th></tr></thead>
          <tbody className="divide-y divide-neutral-200 bg-white">{types.map(([type, job, verify]) => <tr key={type}><th className="px-4 py-4 align-top font-black text-neutral-950">{type}</th><td className="px-4 py-4 align-top text-neutral-700">{job}</td><td className="px-4 py-4 align-top text-neutral-700">{verify}</td></tr>)}</tbody>
        </table>
      </div>
      <h3 className="mt-8 text-lg font-black text-neutral-950">A pilot should answer five questions</h3>
      <div className="mt-4 grid gap-3 md:grid-cols-2">{pilot.map(([label, value]) => <div key={label} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4"><p className="font-black text-neutral-950">{label}</p><p className="mt-2 text-sm leading-7 text-neutral-700">{value}</p></div>)}</div>
      <div className="mt-6 rounded-2xl border border-blue-200 bg-blue-50 p-5">
        <h3 className="font-black text-neutral-950">Keep the denominator honest</h3>
        <p className="mt-2 text-sm leading-7 text-neutral-700">Count a mission only when the intended load reaches the intended destination. Separate empty repositioning, blocked missions, aborted handoffs, and human recoveries so a high fleet utilization number cannot hide low useful output.</p>
      </div>
    </section>
  );
}

function PickingDecisionGuide() {
  const metrics = [
    ["SKU coverage", "Share of the real SKU mix the system will attempt under defined packaging and presentation limits."],
    ["First-attempt grasp", "Picks secured on the first grasp attempt; useful for exposing weak perception or gripper selection."],
    ["Successful placement", "Correct SKU, one item, no damage, correct destination, no human intervention."],
    ["Exception load", "Human interventions per 100 picks plus median time to clear an exception."],
    ["Tail latency", "95th-percentile cycle time, which captures retries and difficult items that averages hide."],
  ];
  const failureTests = [
    "transparent and reflective packaging",
    "deformable bags and loose apparel",
    "dark objects and low-texture surfaces",
    "tight clutter and partially occluded items",
    "porous or damaged surfaces that leak vacuum",
    "double-picks, slips, drops, and wrong-SKU verification",
  ];
  return (
    <section className="soft-card p-5 sm:p-6" aria-labelledby="picking-decision-title">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Picking-system scorecard</p>
      <h2 id="picking-decision-title" className="mt-2 text-2xl font-bold tracking-tight text-neutral-950">Do not buy a warehouse picking robot from picks per hour alone</h2>
      <p className="mt-4 max-w-4xl text-base leading-8 text-neutral-700">A production test should use the warehouse's real order-weighted SKU distribution. A system can look fast while excluding the difficult items, retrying silently, or sending a large exception queue to people.</p>
      <div className="mt-6 grid gap-3 lg:grid-cols-5">{metrics.map(([label, value]) => <div key={label} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4"><p className="font-black text-neutral-950">{label}</p><p className="mt-2 text-sm leading-7 text-neutral-700">{value}</p></div>)}</div>
      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <div className="rounded-2xl border border-neutral-200 p-5"><h3 className="font-black text-neutral-950">Build a hard-item test set</h3><ul className="mt-3 grid gap-2 text-sm leading-7 text-neutral-700">{failureTests.map((item) => <li key={item}>• {item}</li>)}</ul></div>
        <div className="rounded-2xl border border-neutral-200 p-5"><h3 className="font-black text-neutral-950">Cost per successful pick</h3><p className="mt-3 text-sm leading-7 text-neutral-700">Include arm, gripper, vision, integration, safety, maintenance, operator exception time, rejected picks, and upstream item-presentation changes. Divide by verified successful placements, not attempted grasps.</p><p className="mt-3 overflow-x-auto rounded-xl bg-neutral-50 px-4 py-3 font-mono text-xs font-bold text-neutral-800">cost / successful pick = total operating cost / verified successful placements</p></div>
      </div>
    </section>
  );
}

function CobotDecisionGuide() {
  const modes = [
    ["Safety-rated monitored stop", "Robot motion stops while a person is in the collaborative workspace under the validated application."],
    ["Hand guiding", "Operator intentionally guides the robot through a designed hand-guiding interface and safety concept."],
    ["Speed & separation monitoring", "Robot speed or motion changes as separation from a person decreases, using safety-rated sensing and logic."],
    ["Power & force limiting", "Robot/application limits contact forces and pressures within the risk-assessed operating conditions."],
  ];
  const checks = ["payload including tool, adapters, cables, and workpiece", "reach plus wrist moment and center of gravity", "cycle time under validated safety limits", "end-effector and process hazards", "machine/PLC/fieldbus interfaces", "changeover and recovery time", "service, spares, and calibration procedure"];
  return (
    <section className="soft-card overflow-hidden p-5 sm:p-6" aria-labelledby="cobot-decision-title">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Cobot buyer guide</p>
      <h2 id="cobot-decision-title" className="mt-2 text-2xl font-bold tracking-tight text-neutral-950">A collaborative arm does not make the complete application collaborative</h2>
      <p className="mt-4 max-w-4xl text-base leading-8 text-neutral-700">Select the application first. A sharp screwdriver, hot weld, heavy part, crushing fixture, or high-inertia tool can require additional safeguarding even when the arm has force-limiting features.</p>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-200"><table className="min-w-[760px] w-full border-collapse text-left text-sm leading-6"><caption className="sr-only">Collaborative robot operating concepts</caption><thead className="bg-neutral-950 text-white"><tr><th className="px-4 py-3">Collaborative concept</th><th className="px-4 py-3">What it means in the application</th></tr></thead><tbody className="divide-y divide-neutral-200 bg-white">{modes.map(([mode, meaning]) => <tr key={mode}><th className="px-4 py-4 align-top font-black text-neutral-950">{mode}</th><td className="px-4 py-4 align-top text-neutral-700">{meaning}</td></tr>)}</tbody></table></div>
      <h3 className="mt-8 text-lg font-black text-neutral-950">Seven checks before requesting a quote</h3>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{checks.map((item, index) => <div key={item} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4 text-sm leading-7 text-neutral-700"><span className="mr-2 font-black text-blue-700">{String(index + 1).padStart(2, "0")}</span>{item}</div>)}</div>
    </section>
  );
}

function CategoryReferenceGuide({ category, reference, related, relatedNews }) {
  return (
    <section className="mx-auto grid max-w-7xl gap-6 px-4 py-12 sm:px-6 lg:px-8">
      <TextPanel id="how-it-works" title="How these robots work"><p>{reference.operation}</p></TextPanel>
      {reference.comparison && <TextPanel id="comparison" title="How to compare the task"><p>{reference.comparison}</p></TextPanel>}
      <DetailList id="examples" title="Examples and applications" items={reference.examples} />
      {reference.limitations?.length > 0 && <DetailList id="limits" title="Limits of this category" items={reference.limitations} />}
      <DetailList id="deployment-checks" title="What to check for the intended task" items={reference.checks} />
      <section id="sources" className="soft-card p-5 sm:p-6">
        <h2 className="text-xl font-bold text-neutral-950">Sources and further reading</h2>
        <p className="mt-3 text-sm text-neutral-600">Reference updated <time dateTime={reference.reviewed}>{reference.reviewed}</time>.</p>
        {reference.reviewScope && <p className="mt-2 text-sm text-neutral-600">{reference.reviewScope}</p>}
        <ul className="mt-4 grid gap-3">{reference.sources.map(source => <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer" className="font-bold text-blue-700 underline underline-offset-4">{source.label}</a></li>)}</ul>
      </section>
      {!!relatedNews.length && <section className="soft-card p-5 sm:p-6">
        <h2 className="text-xl font-bold text-neutral-950">Related reporting</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">{relatedNews.map(article => <Link key={article.path} to={article.path} className="font-bold text-blue-700">{article.title}</Link>)}</div>
      </section>}
      <section className="soft-card p-5 sm:p-6">
        <h2 className="text-xl font-bold text-neutral-950">Related robot categories</h2>
        <div className="mt-4 flex flex-wrap gap-3">{related.map(item => <Link key={item.slug} to={`/robot-types/${item.slug}`} className="rounded-full border border-neutral-200 px-4 py-2 text-sm font-bold">{item.name}</Link>)}</div>
      </section>
    </section>
  );
}

export default function RobotCategoryPage() {
  const { slug } = useParams();
  const readerGuide = readerGuideByPath[`/robot-types/${slug}`];
  if (readerGuide) return <ReaderGuidePage guide={readerGuide} />;
  const category = robotCategories.find((item) => item.slug === slug);

  if (!category) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-neutral-950">
          Robot category not found
        </h1>
        <p className="mt-4 text-neutral-600">
          This robot category does not exist in the TechniaHQRobot robotics index.
        </p>
        <Link
          to="/robot-types"
          className="mt-6 inline-flex rounded-full bg-blue-600 px-5 py-3 text-sm font-bold text-white"
        >
          Back to robot types
        </Link>
      </section>
    );
  }

  const reference = categoryReferences[slug];
  const hasPrimarySources = (category.sources?.length || 0) >= 3;

  const related = category.relatedCategories
    .map((relatedSlug) =>
      robotCategories.find((item) => item.slug === relatedSlug),
    )
    .filter(Boolean);
  const humanoidNewsSlugs = new Set([
    "humanoid-robots",
    "humanoid-robot-hands",
    "robotic-grippers-and-end-effectors",
  ]);
  const relatedNews = [...xRoboticsArticles, ...newsArticles]
    .filter((article) => {
      if (!article.path) return false;
      return article.categorySlugs?.includes(category.slug);
    })
    .sort((a, b) =>
      String(b.datePublished || "").localeCompare(
        String(a.datePublished || ""),
      ),
    )
    .slice(0, 8);

  const path = `/robot-types/${category.slug}`;
  const breadcrumbs =
    category.slug === "humanoid-robots"
      ? [
          { label: "Home", to: "/" },
          { label: "Humanoid Robots", to: path },
        ]
      : [
          { label: "Home", to: "/" },
          { label: "Robot Types", to: "/robot-types" },
          { label: category.name, to: path },
        ];

  const canonical = `${seoConfig.baseUrl}${path}`;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: category.seoTitle,
    description: reference?.definition || category.seoDescription,
    url: canonical,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonical,
    },
    author: {
      "@type": "Organization",
      name: "TechniaHQRobot",
      url: `${seoConfig.baseUrl}/about-techniahq`,
    },
    publisher: {
      "@type": "Organization",
      name: "TechniaHQRobot",
      url: `${seoConfig.baseUrl}/`,
    },
    dateModified: reference?.reviewed || category.lastUpdated || undefined,
    about: {
      "@type": "Thing",
      name: category.name,
      description: reference?.definition || category.definition,
    },
    keywords: reference ? undefined : category.keywords?.join(", "),
  };
  const schemas = [
    buildBreadcrumbSchema(breadcrumbs),
    articleSchema,
  ].filter(Boolean);

  return (
    <>
      <SEO
        title={category.seoTitle}
        description={reference?.definition || category.seoDescription}
        path={path}
        type="article"
        modifiedTime={reference?.reviewed || category.lastUpdated}
        author="TechniaHQRobot"
        section="Robot types"
        tags={reference ? [] : category.keywords || []}
        schema={schemas}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        eyebrow={category.group}
        title={category.name}
        description={reference?.definition || category.shortDescription}
      />

      {category.slug === "humanoid-robots" && (
        <section className="mx-auto max-w-7xl px-4 pt-10 sm:px-6 lg:px-8">
          <Link to="/robotics/humanoid-robot-data" className="group block rounded-3xl border border-blue-200 bg-blue-50 p-6 transition hover:border-blue-400 sm:p-8">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Humanoid robot data</p>
            <h2 className="mt-3 text-2xl font-black text-neutral-950">Open 20 charts with local source files</h2>
            <p className="mt-3 max-w-4xl leading-7 text-neutral-700">Compare verified prices, dimensions, availability and catalog use cases, then inspect draft architectures for reliability, deployment, funding and operating data.</p>
            <span className="mt-4 inline-flex font-black text-blue-700 group-hover:underline">Browse the infographic library →</span>
          </Link>
        </section>
      )}

      {reference ? <CategoryReferenceGuide category={category} reference={reference} related={related} relatedNews={relatedNews} /> : hasPrimarySources ? (
      <section className="mx-auto grid max-w-7xl gap-6 px-4 py-12 sm:px-6 lg:px-8">
        <ArticleAd placement="top" articleSlug={category.slug} />
        <PageNavigation category={category} />
        <QuickDecisionSummary category={category} />
        <article id="overview" className="soft-card scroll-mt-28 bg-neutral-50/90 p-6 sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-neutral-500">
              Research brief
            </p>
            {category.lastUpdated && (
              <p className="text-xs font-bold text-neutral-500">
                Updated {new Date(`${category.lastUpdated}T00:00:00`).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            )}
          </div>
          <h2 className="mt-3 text-2xl font-bold tracking-tight text-neutral-950">
            Overview
          </h2>
          <p className="mt-4 text-base leading-8 text-neutral-700">
            {category.longDescription}
          </p>
          <p className="mt-4 text-base leading-8 text-neutral-700">
            {category.researchBrief}
          </p>
        </article>

        {category.slug === "agv-robots" && <AgvDecisionGuide />}
        {category.slug === "warehouse-robots-amrs" && <AmrDecisionGuide />}
        {category.slug === "warehouse-picking-robots" && <PickingDecisionGuide />}
        {category.slug === "collaborative-robots-cobots" && <CobotDecisionGuide />}

        {!!relatedNews.length && (
          <section className="soft-card p-5 sm:p-6">
            <h2 className="text-xl font-bold tracking-tight text-neutral-950">
              {humanoidNewsSlugs.has(category.slug) ? "Humanoid robot news and technical analysis" : `${category.name} news and technical analysis`}
            </h2>
            <p className="mt-3 text-sm leading-7 text-neutral-600">
              These articles connect this guide to named robots, technical demonstrations, deployment evidence, and updates relevant to this category.
            </p>
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {relatedNews.map((article) => (
                <Link
                  key={article.path}
                  to={article.path}
                  className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-black leading-6 text-neutral-900 hover:border-blue-200 hover:text-blue-700"
                >
                  {article.title}
                </Link>
              ))}
            </div>
          </section>
        )}

        <TextPanel id="how-it-works" title="How it works">
          <p>{category.howItWorks}</p>
        </TextPanel>

        <DetailList id="applications" title="Real world applications" items={category.useCases} />
        <DetailList id="technology" title="Key technologies" items={category.keyTechnologies} />
        <DetailList title="Sensors commonly used" items={category.sensors} />
        <DetailList
          title="Actuators or movement system"
          items={category.actuators}
        />
        <DetailList title="AI and software used" items={category.software} />
        <DetailList title="Current limitations" items={category.limitations} />
        <DetailList
          title="Popular examples and reference styles"
          items={category.examples}
        />

        <div className="grid gap-6 lg:grid-cols-2">
          <ResearchList title="Failure modes" items={category.failureModes} />
          <ResearchList
            title="Technical bottlenecks"
            items={category.technicalBottlenecks}
          />
        </div>

        <TextPanel id="safety" title="Safety, ethics, and responsible use">
          <p>{category.safetyEthics}</p>
        </TextPanel>
        {!!category.sources?.length && (
          <section id="sources" className="soft-card scroll-mt-28 p-5 sm:p-6">
            <h2 className="text-xl font-bold tracking-tight text-neutral-950">
              Official sources and further reading
            </h2>
            <p className="mt-3 text-sm leading-7 text-neutral-600">
              The sources cover named systems and reference documents. The
              proposed checks do not report hands-on results. Capabilities
              depend on the chosen configuration and operating conditions.
            </p>
            <ul className="mt-5 grid gap-3 md:grid-cols-2">
              {category.sources.map((source) => (
                <li key={source.url}>
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="block rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-bold leading-6 text-blue-700 hover:border-blue-300 hover:bg-blue-50"
                  >
                    {source.label}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="soft-card p-5 sm:p-6">
          <h2 className="text-xl font-bold tracking-tight text-neutral-950">
            Recommended next guides
          </h2>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/robotics-software"
              className="rounded-full border border-neutral-200 px-4 py-2 text-sm font-bold hover:border-blue-200"
            >
              Robotics software
            </Link>
            <Link
              to="/robot-sensors"
              className="rounded-full border border-neutral-200 px-4 py-2 text-sm font-bold hover:border-blue-200"
            >
              Robot sensors
            </Link>
            <Link
              to="/robot-components"
              className="rounded-full border border-neutral-200 px-4 py-2 text-sm font-bold hover:border-blue-200"
            >
              Robot components
            </Link>
          </div>
        </section>

        <section className="soft-card p-5 sm:p-6">
          <h2 className="text-xl font-bold tracking-tight text-neutral-950">
            Related robot categories
          </h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {related.map((item) => (
              <Link
                key={item.slug}
                to={`/robot-types/${item.slug}`}
                className="rounded-full border border-neutral-200 px-4 py-2 text-sm font-bold hover:border-blue-200"
              >
                {item.name}
              </Link>
            ))}
            <Link
              to="/robot-sensors"
              className="rounded-full border border-neutral-200 px-4 py-2 text-sm font-bold hover:border-blue-200"
            >
              Robot Sensors
            </Link>
            <Link
              to="/robotics-topics"
              className="rounded-full border border-neutral-200 px-4 py-2 text-sm font-bold hover:border-blue-200"
            >
              Robotics Topics
            </Link>
          </div>
        </section>

        <ArticleAd placement="bottom" articleSlug={category.slug} />
      </section>
      ) : (
        <section className="mx-auto grid max-w-7xl gap-6 px-4 py-12 sm:px-6 lg:px-8" aria-labelledby="category-overview-title">
          <nav className="soft-card p-5 sm:p-6" aria-label={`Sections in the ${category.name} reference`}>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">On this page</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {[
                ["category-overview", "Overview"],
                ["how-it-works", "How it works"],
                ["applications", "Applications"],
                ["technology", "Technology"],
                ["deployment-checks", "Deployment checks"],
              ].map(([id, label]) => (
                <a key={id} href={`#${id}`} className="rounded-full border border-neutral-200 bg-neutral-50 px-4 py-2 text-sm font-bold text-neutral-800 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700">
                  {label}
                </a>
              ))}
            </div>
          </nav>

          <article id="category-overview" className="soft-card scroll-mt-28 bg-neutral-50/90 p-6 sm:p-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Category reference</p>
              {category.lastUpdated && (
                <p className="text-xs font-bold text-neutral-500">
                  Updated {new Date(`${category.lastUpdated}T00:00:00`).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              )}
            </div>
            <h2 id="category-overview-title" className="mt-3 text-2xl font-bold tracking-tight text-neutral-950">
              What {category.name.toLowerCase()} are
            </h2>
            <p className="mt-4 max-w-4xl text-base leading-8 text-neutral-700">{category.definition}</p>
          </article>

          <TextPanel id="how-it-works" title={`How ${category.name.toLowerCase()} work`}>
            <p>{category.howItWorks}</p>
          </TextPanel>

          <div className="grid gap-6 lg:grid-cols-2">
            <DetailList id="applications" title="Typical applications" items={category.useCases.slice(0, 6)} />
            <DetailList title="Representative systems and formats" items={category.examples.slice(0, 6)} />
            <DetailList id="technology" title="Key technologies" items={category.keyTechnologies.slice(0, 6)} />
            <DetailList title="Common sensors" items={category.sensors.slice(0, 6)} />
            <DetailList title="Actuation and movement" items={category.actuators.slice(0, 6)} />
            <DetailList title="Software functions" items={category.software.slice(0, 6)} />
          </div>

          <section id="deployment-checks" className="soft-card scroll-mt-28 p-5 sm:p-6">
            <h2 className="text-xl font-bold tracking-tight text-neutral-950">What to verify before deployment</h2>
            <p className="mt-3 max-w-4xl text-sm leading-7 text-neutral-600">
              Read the limits below alongside our <Link to="/editorial-policy#category-guides" className="font-bold text-blue-700 underline underline-offset-4">guide to using category references</Link>.
            </p>
            <ul className="mt-5 grid gap-3 md:grid-cols-2">
              {category.limitations.slice(0, 6).map((item) => (
                <li key={item} className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm leading-7 text-neutral-700">
                  {item}
                </li>
              ))}
            </ul>
          </section>

          {!!relatedNews.length && (
            <section className="soft-card p-5 sm:p-6">
              <h2 className="text-xl font-bold tracking-tight text-neutral-950">Related reporting with named evidence</h2>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {relatedNews.map((article) => (
                  <Link key={article.path} to={article.path} className="rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-black leading-6 text-neutral-900 hover:border-blue-200 hover:text-blue-700">
                    {article.title}
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section className="soft-card p-5 sm:p-6">
            <h2 className="text-xl font-bold tracking-tight text-neutral-950">Continue with reviewed resources</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/robotics-software" className="rounded-full border border-neutral-200 px-4 py-2 text-sm font-bold hover:border-blue-200">Robotics software</Link>
              <Link to="/robot-sensors" className="rounded-full border border-neutral-200 px-4 py-2 text-sm font-bold hover:border-blue-200">Robot sensors</Link>
              <Link to="/robot-components" className="rounded-full border border-neutral-200 px-4 py-2 text-sm font-bold hover:border-blue-200">Robot components</Link>
              <Link to="/robotics-topics" className="rounded-full border border-neutral-200 px-4 py-2 text-sm font-bold hover:border-blue-200">Robotics topics</Link>
              {related.map((item) => (
                <Link key={item.slug} to={`/robot-types/${item.slug}`} className="rounded-full border border-neutral-200 px-4 py-2 text-sm font-bold hover:border-blue-200">
                  {item.name}
                </Link>
              ))}
            </div>
          </section>
        </section>
      )}

      {hasPrimarySources && (
        <CTASection title={`Follow TechniaHQRobot for deeper analysis on ${category.name}`} />
      )}
    </>
  );
}
